// vpn-web-bo/src/pages/promo-codes/promoFields.tsx
import { useEffect, useState } from 'react';
import { Alert, Form as AntForm, InputNumber, Radio, Select, Space } from 'antd';
import { Controller, useFormContext } from 'react-hook-form';
import { authFetch } from '../../utils/api';
import {
  MAX_VALIDITY_SECONDS,
  minutesToSeconds,
  secondsToMinutes,
} from '../../schemas/promoCode';

/**
 * VPN-81 兌換碼表單「補缺欄位」共用元件（PromoCodeFormModal + BatchGenerateModal 共用，
 * 避免兩份手刻飄移）。三者都靠外層 <FormProvider> 提供的 RHF context 運作，
 * 欄位驗證仍集中在各自的 zod schema（本檔不重複驗證）。
 */

const errMsg = (
  errors: Record<string, unknown>,
  name: string
): string | undefined =>
  (errors[name] as { message?: string } | undefined)?.message;

/**
 * 有效時長輸入 — 以「分鐘」為輸入單位（Jira 第九章要求），但寫入的權威欄位仍是
 * `validitySeconds`（秒）。輸入支援小數（0.1 分 = 6 秒），送出前 round 成整數秒。
 *
 * 設計選擇：不新增 `validityMinutes` schema 欄位，而是直接把分鐘輸入綁到 validitySeconds
 * 的 Controller（value = 秒/60 顯示、onChange = round(分×60) 寫回）。好處是既有任何秒值
 * （含非 6 的倍數）都能無損載入編輯，不會被「分鐘只准 1 位小數」的 refine 擋住 —— 對齊
 * 任務「保持既有秒值正確」。分鐘僅為輸入層 UI，秒才是權威。
 */
export function ValidityMinutesField() {
  const { control, watch, formState } = useFormContext();
  const seconds = watch('validitySeconds') as number | undefined;
  const err = errMsg(formState.errors as Record<string, unknown>, 'validitySeconds');
  const secondsLabel = typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : 0;
  return (
    <AntForm.Item
      label="有效時長 (分鐘)"
      required
      validateStatus={err ? 'error' : undefined}
      help={
        err ??
        `= ${secondsLabel} 秒（權威單位，存 validitySeconds）。支援小數：0.1 分 = 6 秒 / 1 分 = 60 秒 / 60 分 = 1 小時 / 1440 分 = 1 天`
      }
    >
      <Controller
        name="validitySeconds"
        control={control}
        render={({ field }) => (
          <InputNumber
            style={{ width: '100%' }}
            min={secondsToMinutes(1)}
            max={secondsToMinutes(MAX_VALIDITY_SECONDS)}
            step={0.1}
            placeholder="例如：0.1（6 秒）/ 1440（1 天）"
            value={
              typeof field.value === 'number'
                ? secondsToMinutes(field.value)
                : undefined
            }
            onBlur={field.onBlur}
            onChange={(v) => {
              const m = typeof v === 'number' ? v : v != null ? Number(v) : NaN;
              field.onChange(
                Number.isFinite(m) ? minutesToSeconds(m) : field.value
              );
            }}
          />
        )}
      />
    </AntForm.Item>
  );
}

/** VPN-81 B2 — 單裝置兌換次數上限（0 = 不限）。 */
export function PerDeviceRedeemLimitField() {
  const { control, formState } = useFormContext();
  const err = errMsg(
    formState.errors as Record<string, unknown>,
    'perDeviceRedeemLimit'
  );
  return (
    <AntForm.Item
      label="單裝置兌換次數上限"
      validateStatus={err ? 'error' : undefined}
      help={err ?? '0 代表不限制。與「單裝置累計上限 (MB)」並存（兩者皆須通過）。'}
    >
      <Controller
        name="perDeviceRedeemLimit"
        control={control}
        render={({ field }) => (
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            placeholder="0 = 不限"
            value={field.value as number}
            onBlur={field.onBlur}
            onChange={(v) => field.onChange(typeof v === 'number' ? v : 0)}
          />
        )}
      />
    </AntForm.Item>
  );
}

/**
 * VPN-81 B5 — 節點等級 ACL（軸3）。後端 entity 型別 number[] | null，語意「三態」：
 *   - `null`（含 undefined）= 不限：所有等級節點皆可用此碼（零回歸預設）；
 *   - `[]` = deny-all：空白名單 = 全部拒絕（沒有任何節點可用此碼）；
 *   - `[t…]` = 白名單：只有 node.minTier ∈ 清單的節點可用。
 *
 * 🔴 安全破口修正（VPN-81 codex P1）：舊版用單一 tags Select，把 `[]` 與 `null` 都呈現為
 * 「空選」、且 onChange 把空選塌成 `null`。後果：既有 `[]`（deny-all）的碼開編輯表單、
 * 不動 ACL 直接儲存，會被悄悄放寬成 `null`（不限）→ 白名單失效的安全破口。
 *
 * 修法：改用「模式選擇（Radio）+ 白名單輸入」兩段式，讓 `null` 與 `[]` 在 UI 上明確可辨、
 * 且各自往返無損：
 *   - 模式 = 由 field.value 推導：`== null` → 不限；否則（含 `[]`）→ 指定白名單。
 *   - 切到「不限」→ onChange(null)；切到「指定白名單」→ 保留既有陣列或起始 `[]`。
 *   - 白名單 Select 的 onChange 直接寫回陣列（**空選保持 `[]` 不塌成 null**）。
 *   - 白名單模式下清單為空 → 明確警示「全部拒絕」，避免誤存 deny-all。
 * 載入既有 `[]` 的碼 → 呈現「指定白名單 + 空清單 + 全部拒絕警示」，不動即儲存仍是 `[]`；
 * 要改「不限」必須使用者顯式切到「不限」模式。
 *
 * Select 用 mode="tags"：admin 既能從「現有節點等級」建議選項挑，也能手動輸入尚未有節點的
 * 等級數字（node.minTier 是自由整數 0-999，無固定列舉）。
 */
export function AllowedNodeTiersField({ tierOptions }: { tierOptions: number[] }) {
  const { control, formState } = useFormContext();
  const err = errMsg(
    formState.errors as Record<string, unknown>,
    'allowedNodeTiers'
  );
  return (
    <AntForm.Item
      label="可用節點等級 (ACL)"
      validateStatus={err ? 'error' : undefined}
      help={
        err ??
        '不限 = 所有等級節點皆可用。指定白名單 = 只有等級在清單內的節點可用此碼（填節點「最低等級」數字）；白名單留空 = 全部拒絕（無節點可用）。'
      }
    >
      <Controller
        name="allowedNodeTiers"
        control={control}
        render={({ field }) => {
          // 三態推導：null/undefined → 不限；陣列（含 []）→ 指定白名單。
          const isWhitelist = Array.isArray(field.value);
          const list = isWhitelist ? (field.value as number[]) : [];
          const isDenyAll = isWhitelist && list.length === 0;
          return (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Radio.Group
                value={isWhitelist ? 'whitelist' : 'unlimited'}
                onChange={(e) => {
                  if (e.target.value === 'unlimited') {
                    field.onChange(null);
                  } else {
                    // 切到白名單：保留既有陣列，否則起始空 []（= deny-all，靠下方警示提醒）。
                    field.onChange(Array.isArray(field.value) ? field.value : []);
                  }
                }}
                options={[
                  { label: '不限（所有節點）', value: 'unlimited' },
                  { label: '指定白名單', value: 'whitelist' },
                ]}
                optionType="button"
              />
              {isWhitelist && (
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="輸入節點最低等級數字（留空 = 全部拒絕）"
                  tokenSeparators={[',', ' ']}
                  options={tierOptions.map((t) => ({
                    value: String(t),
                    label: `等級 ${t}`,
                  }))}
                  value={list.map((n) => String(n))}
                  onChange={(vals: string[]) => {
                    const nums = Array.from(
                      new Set(
                        vals
                          .map((v) => Number(v))
                          .filter((n) => Number.isInteger(n) && n >= 0)
                      )
                    );
                    // 白名單模式：直接寫回陣列，空選保持 []（deny-all），不塌成 null。
                    field.onChange(nums);
                  }}
                />
              )}
              {isDenyAll && (
                <Alert
                  type="warning"
                  showIcon
                  message="空白名單 = 全部拒絕：目前沒有任何節點可用此碼。若要放行所有節點，請切換到「不限」。"
                />
              )}
            </Space>
          );
        }}
      />
    </AntForm.Item>
  );
}

/**
 * 從現有節點清單推導「節點等級」建議選項（distinct node.minTier，升冪）。open 時抓一次，
 * 失敗靜默降級成空建議（AllowedNodeTiersField 仍可手動輸入）。
 */
export function useNodeTierOptions(open: boolean): number[] {
  const [opts, setOpts] = useState<number[]>([]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        // pageSize 上限 100（後端 OOM 根治的分頁 cap）；節點實際僅 ~50 個，一頁裝完。
        const res = await authFetch('/api/admin/nodes?page=1&pageSize=100');
        const r = await res.json();
        const list = Array.isArray(r?.data?.list) ? r.data.list : [];
        const tiers = Array.from(
          new Set(
            list
              .map((n: { minTier?: number }) => Number(n.minTier))
              .filter((t: number) => Number.isInteger(t) && t >= 0)
          )
        ).sort((a, b) => (a as number) - (b as number)) as number[];
        if (!cancelled) setOpts(tiers);
      } catch {
        /* 靜默降級：無建議選項，仍可手動輸入節點等級數字 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);
  return opts;
}
